import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, RefreshCw, LogOut, Lock } from "lucide-react";
import { toast } from "sonner";

const TOKEN_KEY = "physimetry_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        api.get("/admin/members", { headers: { Authorization: `Bearer ${t}` } }),
        api.get("/stats"),
      ]);
      setMembers(m.data);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Failed to load members");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (ev) => {
    ev.preventDefault();
    setLoggingIn(true);
    setAuthError("");
    try {
      const res = await api.post("/admin/login", { password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (e) {
      setAuthError(e.response?.status === 401 ? "Invalid password" : "Login failed. Try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMembers([]);
    setStats(null);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export", {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "physimetry_founding_members.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 font-body">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-black/60 backdrop-blur-xl p-8" data-testid="admin-login-form">
          <img src="/assets/logo.png" alt="PHYSIMETRY" data-testid="admin-login-logo" className="h-12 w-auto" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-white/50 mt-2">Admin access</p>
          <div className="mt-8">
            <label htmlFor="admin-password" className="text-xs uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
              <Lock className="h-3.5 w-3.5 text-volt" /> Password
            </label>
            <Input
              id="admin-password"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 bg-white/5 border-white/10 text-white focus-visible:ring-volt"
              autoFocus
            />
            {authError && <p className="mt-2 text-xs text-red-400" data-testid="admin-login-error">{authError}</p>}
          </div>
          <Button
            type="submit"
            disabled={loggingIn || !password}
            data-testid="admin-login-button"
            className="mt-6 w-full bg-volt hover:bg-volt-dark text-white font-display uppercase tracking-wide rounded-none"
          >
            {loggingIn ? "Checking…" : "Enter dashboard"}
          </Button>
          <a href="/" data-testid="admin-back-link" className="mt-4 block text-center text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-volt transition-colors">
            Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white font-body px-6 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <img src="/assets/logo.png" alt="PHYSIMETRY" data-testid="admin-logo" className="h-12 w-auto" />
            <p className="text-[10px] uppercase tracking-[0.35em] text-white/50 mt-2">Founding 100 dashboard</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => load(token)} variant="outline" data-testid="admin-refresh-button" className="border-white/15 text-white hover:bg-white/5 rounded-none">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button onClick={exportCsv} data-testid="admin-export-button" className="bg-volt hover:bg-volt-dark text-white rounded-none">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button onClick={logout} variant="outline" data-testid="admin-logout-button" className="border-white/15 text-white hover:bg-white/5 rounded-none">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="admin-stats">
          <div className="bg-[#050505] p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50">Founding members</p>
            <p className="mt-3 font-display text-5xl text-volt" data-testid="admin-founding-count">
              {stats ? stats.founding_count : "—"}<span className="text-white/30 text-2xl"> / 100</span>
            </p>
          </div>
          <div className="bg-[#050505] p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50">Reserve list</p>
            <p className="mt-3 font-display text-5xl" data-testid="admin-overflow-count">{stats ? stats.overflow_count : "—"}</p>
          </div>
          <div className="bg-[#050505] p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50">Total leads</p>
            <p className="mt-3 font-display text-5xl" data-testid="admin-total-count">{stats ? stats.total : "—"}</p>
          </div>
        </div>

        <div className="mt-10 border border-white/10 overflow-x-auto" data-testid="admin-members-table">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50 uppercase text-[11px] tracking-[0.2em]">Name</TableHead>
                <TableHead className="text-white/50 uppercase text-[11px] tracking-[0.2em]">Email</TableHead>
                <TableHead className="text-white/50 uppercase text-[11px] tracking-[0.2em]">Phone</TableHead>
                <TableHead className="text-white/50 uppercase text-[11px] tracking-[0.2em]">Address</TableHead>
                <TableHead className="text-white/50 uppercase text-[11px] tracking-[0.2em]">Status</TableHead>
                <TableHead className="text-white/50 uppercase text-[11px] tracking-[0.2em]">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.length === 0 && (
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableCell colSpan={6} className="text-center text-white/40 py-12" data-testid="admin-empty-state">
                    No members yet — share the link.
                  </TableCell>
                </TableRow>
              )}
              {members.map((m) => (
                <TableRow key={m.id} className="border-white/10 hover:bg-white/5" data-testid={`admin-member-row-${m.id}`}>
                  <TableCell className="text-white font-medium">{m.full_name}</TableCell>
                  <TableCell className="text-white/70">{m.email}</TableCell>
                  <TableCell className="text-white/70">{m.phone || "—"}</TableCell>
                  <TableCell className="text-white/70 max-w-[200px] truncate">{m.address}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] ${m.status === "founding" ? "bg-volt/15 text-volt border border-volt/40" : "bg-white/5 text-white/60 border border-white/15"}`}>
                      {m.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-white/50 text-xs whitespace-nowrap">
                    {m.created_at ? new Date(m.created_at).toLocaleString() : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
